import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import {
  scriptsAdd,
  scriptsGetById,
  scriptsUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';
import FormTextarea from '../../../components/form/FormTextarea';

const AddEditScriptsModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [preview, setPreview] = useState('');

  const initialValues = {
    name: '',
    status: 'true',
    type: 'text',
    type_option: ''
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('name is required'),
    type_option: Yup.string().required('type option required')
  });

  const onSubmit = async (values) => {
    const formData = new FormData();

    const base64ToFile = (base64String, filename = 'upload.jpg') => {
      try {
        const arr = base64String.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
      } catch (e) {
        console.error('Invalid base64 data:', e);
        return null;
      }
    };

    Object.keys(values).forEach((key) => {
      const value = values[key];

      if (value === null || value === undefined) return;

      if (values.type === 'image' && key === 'type_option') {
        if (value instanceof FileList && value.length > 0) {
          formData.append(key, value[0]);
        } else if (typeof value === 'string' && value.startsWith('data:')) {
          const file = base64ToFile(value);
          if (file) formData.append(key, file);
        } else if (value instanceof File) {
          formData.append(key, value);
        }
        return;
      }
      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    try {
      let response;
      if (isEdit) {
        response = await dispatch(
          scriptsUpdate({ id: editRecord.id, formData: formData })
        ).unwrap();
        if (response?.message) {
          toast.success('Record Updated Successfully');
        } else {
          toast.error(response?.error || 'Update failed');
        }
      } else {
        response = await dispatch(scriptsAdd(formData)).unwrap();
        if (response?.message) {
          toast.success('Record Added Successfully');
        } else {
          toast.error(response?.error || 'Add failed');
        }
      }

      if (response?.message) {
        onCloseClick(true);
        resetForm();
      }
    } catch (error) {
      console.error('Error during submit:', error);
      toast.error('Something went wrong while submitting.');
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchScriptsById = async () => {
    try {
      const response = await dispatch(scriptsGetById(editRecord?.id)).unwrap();
      setValues({
        ...values,
        name: response?.name,
        status: String(response?.status),
        type: response?.type,
        type_option: response?.type_option
      });
      setPreview(response?.type === 'image' ? response?.type_option : '');
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchScriptsById();
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Scripts' : 'Add Scripts'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="8">
              <Row>
                <Col lg="8">
                  <FormInput
                    name="name"
                    label="Name"
                    placeholder="Enter name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="4">
                  <FormToggle
                    name="status"
                    label="Status"
                    value={values.status}
                    onChange={(e) => setFieldValue('status', e)}
                  />
                </Col>
                <Col lg="8">
                  <FormSelect
                    name="type"
                    label="Type"
                    placeholder="Select Type"
                    value={values.type}
                    options={[
                      { label: 'Text', value: 'text' },
                      { label: 'Image', value: 'image' }
                    ]}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue('type_option', '');
                    }}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    menuPlacement="bottom"
                  />
                </Col>
                <Col lg="8">
                  {values.type === 'text' ? (
                    <FormTextarea
                      name="type_option"
                      label="Script"
                      placeholder="Enter Script"
                      value={values.type_option}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      rows={2}
                    />
                  ) : (
                    <FormGroup>
                      <Label htmlFor="prompt-file-upload">Script File</Label>
                      <div className="file-upload-container cursor-pointer">
                        <Input
                          type="text"
                          className={`file-name cursor-pointer ${errors['type_option'] ? 'error-input' : ''}`}
                          placeholder={
                            values.type_option && values.type_option.name
                              ? values.type_option.name
                              : 'Choose a file'
                          }
                          readOnly
                        />
                        <label className="upload-button cursor-pointer">
                          <Icon.Upload size={18} className="cursor-pointer" />
                          <Input
                            type="file"
                            id="prompt-file-upload"
                            className="file-upload cursor-pointer"
                            accept="image/png, image/jpeg, image/jpg"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setFieldValue('type_option', file);
                                  setPreview(reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      </div>
                      {errors['type_option'] && (
                        <span className="error-message">{errors['type_option']}</span>
                      )}
                    </FormGroup>
                  )}
                </Col>
              </Row>
            </Col>
            <Col lg="4">
              {values?.type === 'image' && values.type_option !== '' && preview ? (
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    maxHeight: '350px'
                  }}
                />
              ) : null}
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditScriptsModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditScriptsModal;
